import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQuery } from '@/helpers/useQuery';
import { mutateListOnPost } from '@/helpers/swr';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import PageLoading from '@/components/layout/PageLoading';
import Error from '@/components/common/Error';
import Stepper from '@/components/common/Stepper';

import BasicInfoForm from './components/common/BasicInfoForm';
import LocationDetailsForm from './components/common/LocationDetailsForm';
import OfferingDetailsForm from './components/common/OfferingDetailsForm';
import PricingAndBookingForm from './components/common/PricingAndBookingForm';

import { OFFERING_CURRENCY } from './enums';
import { useCoachActivity } from '@/data/coachActivity';
import { useActivity } from '@/data/activity';
import { useOfferings, usePostOffering } from '@/data/offering';

const AddOfferingForm = () => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const coachActivityId = useQuery('coachActivityId');
  const backTo = useQuery('backTo');
  const navigate = useNavigate();
  const coachActivity = useCoachActivity(coachActivityId);
  const activity = useActivity(coachActivity.data.activityId);
  const offerings = useOfferings({
    coachActivityId: coachActivityId
  });
  const postOffering = usePostOffering();

  const [offeringFormData, setOfferingFormData] = useState({
    // Step 1: Basic Info
    name: '',
    description: '',
    categoryId: '',
    // Step 2: Location
    regionId: '',
    locationId: '',
    isMeetingSpot: false,
    meetingLat: 49.888,
    meetingLng: -119.494,
    locationName: '',
    locationDetails: '',
    // Step 3: Details
    length: '',
    requirements: '',
    includedItems: '',
    notIncludedItems: '',
    //   videoLink: '',
    // Step 4: Pricing
    pricePerClient: '',
    currency: OFFERING_CURRENCY.CAD,
    isGroup: false,
    maxGroupClients: 1,
    bookingLeadTime: 24
  });

  const handleBasicInfoSubmit = (values) => {
    setOfferingFormData((prevData) => ({ ...prevData, ...values }));
    setStep(step + 1);
  };

  const handleLocationDetailsSubmit = (values) => {
    setOfferingFormData((prevData) => ({ ...prevData, ...values }));
    setStep(step + 1);
  };

  const handleOfferingDetailsSubmit = (values) => {
    setOfferingFormData((prevData) => ({ ...prevData, ...values }));
    setStep(step + 1);
  };

  const handlePreviousStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      // Handle form submission
      const payload = {
        ...offeringFormData,
        ...values,
        pricePerClient: Math.round(parseFloat(values.pricePerClient) * 100), // Safely convert string to number
        coachActivityId: coachActivityId,
        activityId: activity.data.id,
        coachId: coachActivity.data.coachId
      };
      const newOffering = await postOffering(payload);
      await offerings.mutate(
        (curr) => mutateListOnPost(curr, newOffering),
        false
      );
      navigate(backTo);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { title: 'Basic Info' },
    { title: 'Location' },
    { title: 'Details' },
    { title: 'Pricing' }
  ];

  return (
    <>
      <Stepper steps={steps.map((s) => s.title)} currentStep={step} />
      <Card>
        <CardHeader>
          <CardTitle>{steps[step].title}</CardTitle>
        </CardHeader>
        <CardContent>
          {step === 0 && (
            <div>
              <BasicInfoForm
                initialValues={{
                  name: offeringFormData.name,
                  description: offeringFormData.description,
                  categoryId: offeringFormData.categoryId
                }}
                onHandleSubmit={handleBasicInfoSubmit}
              />
            </div>
          )}
          {step === 1 && (
            <div>
              <LocationDetailsForm
                initialValues={{
                  regionId: offeringFormData.regionId,
                  locationId: offeringFormData.locationId,
                  isMeetingSpot: offeringFormData.isMeetingSpot,
                  meetingLat: offeringFormData.meetingLat,
                  meetingLng: offeringFormData.meetingLng,
                  locationName: offeringFormData.locationName,
                  locationDetails: offeringFormData.locationDetails
                }}
                onHandleSubmit={handleLocationDetailsSubmit}
                activity={activity.data}
                onPreviousStep={handlePreviousStep}
              />
            </div>
          )}
          {step === 2 && (
            <div>
              <OfferingDetailsForm
                initialValues={{
                  length: offeringFormData.length,
                  requirements: offeringFormData.requirements,
                  includedItems: offeringFormData.includedItems,
                  notIncludedItems: offeringFormData.notIncludedItems
                }}
                onHandleSubmit={handleOfferingDetailsSubmit}
                onPreviousStep={handlePreviousStep}
              />
            </div>
          )}
          {step === 3 && (
            <div>
              <PricingAndBookingForm
                initialValues={{
                  pricePerClient: offeringFormData.pricePerClient,
                  currency: offeringFormData.currency,
                  isGroup: offeringFormData.isGroup,
                  maxGroupClients: offeringFormData.maxGroupClients,
                  bookingLeadTime: offeringFormData.bookingLeadTime
                }}
                onHandleSubmit={handleSubmit}
                onPreviousStep={handlePreviousStep}
                submitting={submitting}
              />
            </div>
          )}
          {apiError && <Error error={apiError} />}
        </CardContent>
      </Card>
    </>
  );
};

export const AddOfferingFormLoading = () => {
  const coachActivityId = useQuery('coachActivityId');
  const coachActivity = useCoachActivity(coachActivityId);
  const activity = useActivity(coachActivity.data.activityId);

  if (activity.isLoading && !activity.data) {
    return <PageLoading />;
  }
  return <AddOfferingForm />;
};

export default AddOfferingFormLoading;
